import { CheckBoxSwitcher, FieldErrorAndDescription, FieldLabel } from '@systemeio/ui'
import { GoogleCalendarSelector } from 'modules/profile/integrations/google/calendar-selector'
import { useCalendars } from 'modules/profile/integrations/google/hooks/use-calendars'
import { useEffect, useLayoutEffect, useState } from 'react'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { twMerge } from 'tailwind-merge'
import { EventLocationTypeEnum } from '../constants'
import { EventLocationInterface } from '../types/event-interface'

interface Props {
  locations: EventLocationInterface[]
  googleCalendarId?: string
  googleIntegrationId?: string
  googleCalendarError?: string
  googleIntegrationError?: string
  setGoogleCalendarId: (id: string | undefined) => void
  setGoogleIntegrationId: (id: string | undefined) => void
}

export function AddToGoogleCalendar({
  locations,
  googleCalendarId,
  googleIntegrationId,
  googleCalendarError,
  googleIntegrationError,
  setGoogleCalendarId,
  setGoogleIntegrationId,
}: Props) {
  const { t } = useLocoTranslation()
  const [switchState, setSwitchState] = useState(!!googleCalendarId)
  const { hasGoogleCalendars, data: calendars } = useCalendars()

  const isGoogleMeetInLocations =
    !!locations?.length &&
    locations.some(location => location.locationType === EventLocationTypeEnum.GoogleMeet)

  const addToGoogleCalendarSelected = switchState || isGoogleMeetInLocations

  useEffect(() => {
    if (googleCalendarId) {
      setSwitchState(true)
    }
  }, [googleCalendarId])

  useLayoutEffect(() => {
    if (addToGoogleCalendarSelected && !googleCalendarId && hasGoogleCalendars && calendars) {
      const defaultCalendar = calendars.find(calendar => calendar.isUsedForSync)
      setGoogleCalendarId(defaultCalendar?.id)
      setGoogleIntegrationId(defaultCalendar?.integration.id)
    }
  }, [addToGoogleCalendarSelected])

  if (!hasGoogleCalendars) return null

  const selectedCalendar = calendars?.find(
    calendar => calendar.id === googleCalendarId && calendar.integration.id === googleIntegrationId,
  )

  return (
    <div className="flex flex-col gap-3">
      <FieldLabel label={t('dashboard.user_settings.integrations_page.google_calendar.name')} />

      <CheckBoxSwitcher
        label={t('dashboard.calendar.add_event_to_google_calendar')}
        selected={addToGoogleCalendarSelected}
        disabled={isGoogleMeetInLocations}
        switcherClassName={twMerge(isGoogleMeetInLocations && 'cursor-not-allowed')}
        onChange={(value: boolean) => {
          setSwitchState(value)
          if (!value) {
            setGoogleCalendarId(undefined)
            setGoogleIntegrationId(undefined)
          }
        }}
      />

      {addToGoogleCalendarSelected && (
        <GoogleCalendarSelector
          label={t('dashboard.calendar.select_calendar')}
          calendar={selectedCalendar}
          onChange={(calendarId, integrationId) => {
            setGoogleCalendarId(calendarId)
            setGoogleIntegrationId(integrationId)
          }}
          className={'max-w-xl'}
        />
      )}

      <FieldErrorAndDescription error={googleCalendarError} />
      <FieldErrorAndDescription error={googleIntegrationError} />
    </div>
  )
}
